import { Vector as VectorSource, XYZ, TileWMS, ImageArcGISRest, Cluster as ClusterSource, WMTS } from "ol/source";
import TileGrid from "ol/tilegrid/TileGrid";
import WMTSTileGrid from "ol/tilegrid/WMTS";
import { Layer, Tile as TileLayer, Vector as VectorLayer, Image as ImageLayer, Group as LayerGroup } from "ol/layer";
import { config, ILayerState, mapTileGrid, wmtsTileGrid } from "./config";
import { getMap } from "./GeoLiteMap";
import { fetchFieldInfo, loadSourceFeatures } from "./rest";
import { tile as tileStrategy } from "ol/loadingstrategy";
import { createXYZ } from "ol/tilegrid";
import { getClusterSymbol, getSimpleSymbol } from "./Symbols/clusterSymbols";
import { LayerType } from "./enums";
import { IFeatureLayerData } from "./interfaces";
import { createClusterSource } from "./clusterFunctions";
import { getCurrentActiveToken, getTokenExtraLayers } from "./MapDownload/Token";
import { getReducedTileGrid } from "./basemapFunctions";

const zIndex = 1000;
const appid = encodeURI(window.location.host + window.location.pathname);

export const getDataLayers = (configA, loadAuthLayers?: boolean): Layer[] => {
    const configResolutions = configA.mapLods && configA.mapLods.map((lod) => lod.resolution);
    const defaultTileGrid = mapTileGrid;
    configResolutions && (defaultTileGrid.resolutions = configResolutions);
    const tileGrid = new TileGrid(defaultTileGrid);

    const hasClusterSharing = configA.layers.find((l) => l.clusterSharingLayer);

    let configLayers = [];
    if (loadAuthLayers) {
        configLayers = getTokenAllowedLayers(configA.layers);
    } else {
        const layersNotRequiringLogin = configA.layers.filter((layer) => !layer.requiresLogin);
        const layersRequiringLogin = getTokenAllowedLayers(configA.layers);
        configLayers = [...layersNotRequiringLogin, ...layersRequiringLogin];
    }

    return configLayers.reverse().map((l, i) => {
        if (l.type === "MapImageLayer") {
            const olLayer = new ImageLayer({
                properties: { id: l.id },
                source: new ImageArcGISRest({
                    url: l.url,
                    crossOrigin: l.crossOrigin || "Anonymous",
                    imageLoadFunction: (image, src) =>
                        ((image.getImage() as HTMLImageElement).src = src.replace("DPI=90", "DPI=96")),
                    attributions: l.copyright,
                    ratio: 1,
                    params: l.visibleLayers && { LAYERS: "show:" + l.visibleLayers.join(",") }
                }),
                zIndex
            });
            olLayer.setProperties(l);
            olLayer.setVisible(false);
            olLayer.set("defaultVisible", l.visible);
            return olLayer;
        }

        if (l.type === "TileLayer") {
            const tg = l.tileGrid
                ? new TileGrid(l.tileGrid)
                : l?.maxScale >= 0 || l?.maxLodLevel
                ? getReducedTileGrid(defaultTileGrid, l.maxScale, l.maxLodLevel || defaultTileGrid.resolutions.length)
                : tileGrid;

            const olLayer = new TileLayer({
                properties: { id: l.id },
                source: new XYZ({
                    url: l.url + "/tile/{z}/{y}/{x}",
                    projection: config.default.projection,
                    crossOrigin: l.crossOrigin || "Anonymous",
                    attributions: l.copyright,
                    tileGrid: tg
                }),
                minResolution: l.minResolution,
                maxResolution: l.maxResolution,
                extent: l.extent,
                zIndex
            });
            olLayer.setProperties(l);
            olLayer.setVisible(false);
            olLayer.set("defaultVisible", l.visible);
            return olLayer;
        }

        if (l.type === "WMS") {
            const olLayer = new TileLayer({
                source: new TileWMS({
                    url: l.url,
                    params: {
                        FORMAT: l.imagetype || "image/vnd.jpeg-png8",
                        LAYERS: l.layers
                    },
                    serverType: l.serverType || "geoserver",
                    crossOrigin: l.crossOrigin || "Anonymous",
                    projection: "EPSG:" + (l.spatialReference || 3059),
                    tileGrid,
                    attributions: l.copyright,
                    hidpi: false
                }),
                properties: { id: l.id },
                zIndex
            });
            olLayer.setProperties(l);
            olLayer.setVisible(false);
            olLayer.set("defaultVisible", l.visible);
            return olLayer;
        }

        if (l.type === "WMTS") {
            const olLayer = new TileLayer({
                source: new WMTS({
                    attributions: l.copyright,
                    url: l.url,
                    layer: l.layers,
                    matrixSet: "LKS_LVM",
                    format: l.imagetype,
                    projection: l.projection,
                    tileGrid: new WMTSTileGrid(wmtsTileGrid),
                    style: l.style || "raster",
                    wrapX: true,
                    crossOrigin: l.crossOrigin || "Anonymous",
                    // https://manidarbi.lvm.lv/browse/GDEV-4137
                    tileLoadFunction: (imageTile, src) => {
                        //@ts-ignore
                        imageTile.getImage().src = src + "&appid=" + appid;
                    }
                }),
                properties: { id: l.id },
                visible: true,
                zIndex
            });
            olLayer.setProperties(l);
            olLayer.setVisible(false);
            olLayer.set("defaultVisible", l.visible);
            return olLayer;
        }

        if (l.type === "ClusterLayer") {
            const clusterMinScaleThreshold = l.cluster?.clusterMinScale || 0;
            const url = l.layerId !== undefined ? l.url + "/" + l.layerId : l.url;
            let olLayer;

            if (hasClusterSharing) {
                olLayer = new VectorLayer({
                    properties: { id: l.id },
                    source: new VectorSource(),
                    minResolution: l.minResolution,
                    maxResolution: l.maxResolution,
                    opacity: 0.01,
                    zIndex
                });
            } else {
                const layerData: IFeatureLayerData = {
                    id: l.id,
                    layerId: l.layerId,
                    cluster:
                        l.cluster ||
                        configA.layers.find((configLayer) => configLayer.id === l.clusterSharingLayer).cluster,
                    vectorStyles: l.vectorStyles,
                    identify: l.identify
                };

                const source = new VectorSource({
                    loader: (ext) => loadSourceFeatures(url, ext, source, layerData),
                    strategy: tileStrategy(createXYZ({ tileSize: 512 })),
                    attributions: l.copyright
                });
                const clusterSource = createClusterSource();
                clusterSource.setSource(source);
                clusterSource.setDistance(l.cluster.distance || 200);
                clusterSource.setMinDistance(l.cluster.minDistance || 0);

                olLayer = new VectorLayer({
                    properties: { id: l.id },
                    source: clusterSource,
                    minResolution: l.minResolution,
                    maxResolution: l.maxResolution,
                    zIndex: zIndex + 100,
                    style: (clusterFeature) => {
                        const clusterFeatures = clusterFeature.get("features");
                        return clusterFeatures?.length > 1
                            ? getClusterSymbol(clusterFeature.get("HOVERED"), clusterFeatures.length, l.cluster)
                            : getSimpleSymbol(clusterFeature, l.vectorStyles);
                    }
                });

                document.addEventListener("moveend", (e: CustomEvent) => {
                    e.detail <= clusterMinScaleThreshold
                        ? olLayer.getSource() instanceof ClusterSource && olLayer.setSource(source)
                        : !(olLayer.getSource() instanceof ClusterSource) && olLayer.setSource(clusterSource);
                });
                l.vectorStyles.labels && olLayer.setZIndex(zIndex + 10);
            }
            fetchFieldInfo(olLayer, url);
            olLayer.setProperties(l);
            olLayer.setVisible(false);
            olLayer.set("defaultVisible", l.visible);
            return olLayer;
        }
        if (l.type === "FeatureLayer") {
            const url = l.url + "/" + l.layerId;
            const source = new VectorSource({
                loader: (ext) => loadSourceFeatures(url, ext, source),
                strategy: tileStrategy(createXYZ({ tileSize: 512 })),
                attributions: l.copyright
            });
            const olLayer = new VectorLayer({
                properties: { id: l.id },
                source,
                minResolution: l.minResolution,
                maxResolution: l.maxResolution,
                zIndex,
                style: (f) => getSimpleSymbol(f, l.vectorStyles),
                declutter: !!l.declutter
            });
            fetchFieldInfo(olLayer, url);
            l.tooltip && olLayer.set("isTooltipLayer", true);
            olLayer.setProperties(l);
            olLayer.setVisible(false);
            olLayer.set("defaultVisible", l.visible);
            l.vectorStyles?.labels &&
                document.addEventListener("moveend", () => olLayer.setStyle((f) => getSimpleSymbol(f, l.vectorStyles)));
            l.vectorStyles?.labels && olLayer.setZIndex(zIndex + 10);
            return olLayer;
        }
        if (l.type === "LayerGroup") {
            const olLayer = new LayerGroup({
                properties: { id: l.id },
                minResolution: l.minResolution,
                maxResolution: l.maxResolution
                // zIndex
            });
            olLayer.setProperties({
                opacity: l.opacity,
                title: l.title,
                type: LayerType.LayerGroup
            });

            olLayer.setProperties(l);
            olLayer.setVisible(false);
            olLayer.set("defaultVisible", l.visible);
            return olLayer;
        }
    });
};

export const switchLayers = (layerStates, parentState?: any) => {
    layerStates.map((state) => {
        const mapLayer = getMap()
            .getLayers()
            .getArray()
            .find((mapLayer) => mapLayer.get("id") === state.layerId);

        // šis ir LayerGroup gadījumam
        if (!mapLayer) {
            state.sublayers && state.sublayers.length && switchLayers(state.sublayers, state);
            return;
        }

        if (mapLayer.get("type") === "MapImageLayer") {
            const toShow = [];
            const toHide = [];

            getVisibility(state, toShow, toHide);

            const hidenIDs = Array.from(new Set(toHide));
            const shownIDs = Array.from(new Set(toShow));
            // šis ir gadījumam, kad konfigā norādīti redzamie slāņi, piem. lvm.lv lapas kartei
            const defaultVisible = mapLayer.get("visibleLayers");
            const combined = shownIDs.filter(
                (item) => !hidenIDs.includes(item) && (defaultVisible ? defaultVisible.includes(item) : true)
            );

            (mapLayer as ImageLayer<ImageArcGISRest>).getSource().updateParams({
                LAYERS: "show:" + (combined.length ? combined : "9999999")
            });
        }

        if (mapLayer.get("type") === "TileLayer") {
            state.visible !== mapLayer.getVisible() && mapLayer.setVisible(state.shown && state.visible);
        }

        if (mapLayer.get("type") === "WMS") {
            parentState
                ? parentState.visible === true
                    ? mapLayer.setVisible(state.visible)
                    : parentState.visible === false
                    ? mapLayer.setVisible(false)
                    : mapLayer.setVisible(state.visible)
                : mapLayer.setVisible(state.shown && state.visible);
        }

        if (mapLayer.get("type") === "WMTS") {
            state.visible !== mapLayer.getVisible() && mapLayer.setVisible(state.visible);
        }
    });
};

const getVisibility = (stateLayer: ILayerState, toShow: number[], toHide: number[]) => {
    if (!stateLayer.visible) {
        hideAllSublayers(stateLayer, toHide);
        return;
    }
    if (stateLayer.sublayers && stateLayer.sublayers.length) {
        for (const subLayer of stateLayer.sublayers) {
            subLayer.sublayers && subLayer.sublayers.length
                ? getVisibility(subLayer, toShow, toHide)
                : subLayer.visible
                ? toShow.push(Number(subLayer.layerId))
                : toHide.push(Number(subLayer.layerId));
        }
    } else {
        stateLayer.visible ? toShow.push(Number(stateLayer.layerId)) : toHide.push(Number(stateLayer.layerId));
    }
};

const hideAllSublayers = (stateLayer: ILayerState, toHide: number[]) => {
    if (!stateLayer.sublayers) {
        return;
    }
    for (const subLayer of stateLayer.sublayers) {
        subLayer.sublayers ? hideAllSublayers(subLayer, toHide) : toHide.push(Number(subLayer.layerId));
    }
};

const getTokenAllowedLayers = (layers) => {
    if (!getCurrentActiveToken()) {
        return [];
    }
    const tokenExtraLayers = getTokenExtraLayers();
    if (!tokenExtraLayers) {
        return layers.filter((layer) => layer.requiresLogin && !layer.id.includes("geotelpaparcels"));
    }
    const extraLayerIds: number[] = tokenExtraLayers.split(";").map((id) => Number(id) - 1);
    return layers.filter(
        (layer) =>
            (layer.requiresLogin && !layer.id.includes("geotelpaparcels")) ||
            (layer.requiresLogin &&
                layer.id.includes("geotelpaparcels") &&
                layer.layerId !== undefined &&
                extraLayerIds.includes(Number(layer.layerId)))
    );
};
